import {Helmet} from '@dr.pogodin/react-helmet';
import {Form, FormField, FormSubmitHandler, Separator} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetInspectionTemplatesQuery,
  useGetSeriesListQuery,
  useGetTenantQuery,
  useListThemeQuery,
} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetServiceCaseTypeQuery,
  usePatchServiceCaseTypeMutation,
  usePostServiceCaseTypeMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {ServiceCaseTypeForm, ServiceCaseTypeFormSchema} from './types/ServiceCaseTypeFormSchema';

export function ServiceCaseTypeDetail() {
  const navigate = useNavigate();
  const {id} = useParams();
  const {data: tenant} = useGetTenantQuery();
  const {
    data: serviceCaseType,
    isLoading: isServiceCaseTypeLoading,
    isError: isServiceCaseTypeError,
  } = useGetServiceCaseTypeQuery({serviceCaseTypeId: id ?? ''}, {skip: isNil(id)});
  const {data: authorizationProfiles, isLoading: isAuthorizationProfilesLoading} =
    useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const {data: series, isLoading: isSeriesLoading} = useGetSeriesListQuery(
    {
      type: ['workshop/service_case', 'workshop/service_order'],
    },
    {
      selectFromResult: (result) => ({
        ...result,
        data: {
          serviceCase: result?.data?.filter((item) => item.type === 'workshop/service_case'),
          serviceOrder: result?.data?.filter((item) => item.type === 'workshop/service_order'),
        },
      }),
    }
  );

  const {data: inspectionTemplates, isLoading: isInspectionTemplatesLoading} =
    useGetInspectionTemplatesQuery({isActive: true});

  const {data: themes, isLoading: isThemesLoading} = useListThemeQuery();

  const [postServiceCaseType] = usePostServiceCaseTypeMutation();
  const [patchServiceCaseType] = usePatchServiceCaseTypeMutation();

  const handleSubmit: FormSubmitHandler<ServiceCaseTypeForm> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      ...data,
      isDefaultServiceOrderVariant: true,
      defaultCheckInInspectionId: data.defaultCheckInInspectionId ?? undefined,
      defaultHandoverInspectionId: data.defaultHandoverInspectionId ?? undefined,
      defaultDigitalCertificateTemplateId: data.defaultDigitalCertificateTemplateId ?? undefined,
    };

    if (isNil(id)) {
      return await postServiceCaseType({body})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.serviceCaseType);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    return await patchServiceCaseType({serviceCaseTypeId: id, body})
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.serviceCaseType);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    name: serviceCaseType?.name ?? undefined,
    authorizationProfileId: serviceCaseType?.authorizationProfileId ?? undefined,
    docSeriesServiceCaseId: serviceCaseType?.docSeriesServiceCaseId ?? undefined,
    docSeriesServiceOrderId: serviceCaseType?.docSeriesServiceOrderId ?? undefined,
    defaultCheckInInspectionId: serviceCaseType?.defaultCheckInInspectionId ?? undefined,
    defaultHandoverInspectionId: serviceCaseType?.defaultHandoverInspectionId ?? undefined,
    defaultDigitalCertificateTemplateId:
      serviceCaseType?.defaultDigitalCertificateTemplateId ?? undefined,
  };

  const titleKey = isNil(id)
    ? 'entity.serviceCase.actions.newServiceCaseType'
    : 'entity.workshopPresetsCategory.labels.serviceCaseType';

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <SettingsTemplate
        header={{
          title: serviceCaseType?.name ?? i18n.t('entity.serviceCase.actions.newServiceCaseType'),
          breadcrumbs: [
            {
              label: i18n.t('entity.workshopPresetsCategory.labels.serviceCaseType'),
              href: settingsRoutes.serviceCaseType,
            },
          ],
        }}
        isCreating={isNil(id)}
        isLoading={isServiceCaseTypeLoading}
        isError={isServiceCaseTypeError}
        data-testid={testIds.settings.serviceCaseTypeDetail('template')}
      >
        <SettingsSection>
          <Form<ServiceCaseTypeForm>
            defaultValues={defaultValues}
            experimentalZodSchema={ServiceCaseTypeFormSchema}
            onSubmit={handleSubmit}
            shouldWatchForUnsavedChanges
          >
            {(control, _formApi, navigateWithoutBlocker) => (
              <VStack spacing={4}>
                <FormField
                  control={control}
                  type="text"
                  name="name"
                  label={i18n.t('entity.serviceCase.labels.serviceCaseTypeName')}
                  isRequired
                  data-testid={testIds.settings.serviceCaseTypeDetail('name')}
                />

                <FormField
                  control={control}
                  type="choice"
                  name="authorizationProfileId"
                  label={i18n.t('entity.settings.labels.authorizationProfile')}
                  options={
                    authorizationProfiles
                      ?.filter((profile) => profile.isActive)
                      ?.map((profile) => ({
                        label: profile?.name,
                        value: profile?.id,
                      })) ?? []
                  }
                  isLoading={isAuthorizationProfilesLoading}
                  isRequired
                  data-testid={testIds.settings.serviceCaseTypeDetail('authorizationProfile')}
                />

                <Separator spacing={0} />

                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      type="choice"
                      name="docSeriesServiceCaseId"
                      label={i18n.t('entity.serviceCase.labels.serviceCaseSeries')}
                      options={
                        series?.serviceCase?.map((series) => ({
                          label: series.name,
                          value: series.id,
                        })) ?? []
                      }
                      isLoading={isSeriesLoading}
                      isRequired
                      data-testid={testIds.settings.serviceCaseTypeDetail('SCSeries')}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      type="choice"
                      name="docSeriesServiceOrderId"
                      label={i18n.t('entity.serviceOrder.labels.serviceOrderSeries')}
                      options={
                        series?.serviceOrder?.map((series) => ({
                          label: series.name,
                          value: series.id,
                        })) ?? []
                      }
                      isLoading={isSeriesLoading}
                      isRequired
                      data-testid={testIds.settings.serviceCaseTypeDetail('orderSeries')}
                    />
                  </Box>
                </HStack>

                <Separator spacing={0} />

                <HStack spacing={4}>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      type="choice"
                      name="defaultCheckInInspectionId"
                      label={i18n.t('entity.serviceCase.labels.defaultCheckInInspection')}
                      options={
                        inspectionTemplates?.map((template) => ({
                          label: template.name,
                          value: template.id,
                        })) ?? []
                      }
                      isLoading={isInspectionTemplatesLoading}
                      data-testid={testIds.settings.serviceCaseTypeDetail('checkInInspection')}
                    />
                  </Box>
                  <Box flex={1}>
                    <FormField
                      control={control}
                      type="choice"
                      name="defaultHandoverInspectionId"
                      label={i18n.t('entity.serviceCase.labels.defaultHandoverInspection')}
                      options={
                        inspectionTemplates?.map((template) => ({
                          label: template.name,
                          value: template.id,
                        })) ?? []
                      }
                      isLoading={isInspectionTemplatesLoading}
                      data-testid={testIds.settings.serviceCaseTypeDetail('handoverInspection')}
                    />
                  </Box>
                </HStack>

                <FormField
                  control={control}
                  type="choice"
                  name="defaultDigitalCertificateTemplateId"
                  label={i18n.t('entity.serviceCase.labels.defaultDigitalCertificate')}
                  options={
                    themes?.map((theme) => ({
                      label: theme.name,
                      value: theme.themeId,
                    })) ?? []
                  }
                  isLoading={isThemesLoading}
                  data-testid={testIds.settings.serviceCaseTypeDetail('digitalCertificate')}
                />

                <SettingsFooter
                  actions={[
                    {
                      type: 'button',
                      title: i18n.t('general.actions.discardChanges'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.serviceCaseType),
                    },
                    {
                      control,
                      type: 'form-button',
                      buttonType: 'submit',
                      title: i18n.t('general.actions.saveChanges'),
                    },
                  ]}
                  data-testid={testIds.settings.serviceCaseTypeDetail('footer')}
                />
              </VStack>
            )}
          </Form>
        </SettingsSection>
      </SettingsTemplate>
    </>
  );
}
